import { Chart } from 'components/Chart/Chart'
import isEqual from 'lodash/isEqual'
import omit from 'lodash/omit'
import PropTypes from 'prop-types'
import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocalStorage } from 'utils/hooks/useLocalStorage'
import { Cards } from 'modules/Statistics/Pulse/components/Cards'
import { COLORS } from 'modules/Statistics/Pulse/constants/colors'
import styles from 'modules/Statistics/Pulse/Pulse.module.scss'
import FilterPanel from 'components/Filters/FilterPanel'
import useFiltersManager from 'utils/hooks/useFiltersManager'
import config from 'modules/Statistics/Pulse/filters'
import { useTranslation } from 'react-i18next'
import { Button, Grid, Typography } from 'gu-ui-kit'
import Switch from 'components/Switch'
import clsx from 'clsx'
import { useBreakpoint } from 'utils/hoc/breakpointProvider'

const initialMetrics = [
  { id: 'cost', isActive: true },
  { id: 'impressions', isActive: true },
  { id: 'clicks', isActive: true },
  { id: 'avg_click_price', isActive: true },
  { id: 'google_analytics_metrics.transactions', isActive: false },
  { id: 'google_analytics_metrics.transaction_revenue', isActive: false },
  { id: 'google_analytics_metrics.roi', isActive: false },
]

const Pulse = () => {
  const dispatch = useDispatch()
  const [metricInited, setMetricInited] = useState(false)
  const { t } = useTranslation()
  const breakpoints = useBreakpoint()
  const [isDemoMode, setIsDemoMode] = useLocalStorage('isDemoMode', false)
  const [metricsConfig, setMetricsConfig] = useLocalStorage('pulseMetricsConfig', initialMetrics)

  const filterManager = useFiltersManager({
    filters: useSelector((state) => state['statistics/pulse'].filters),
    config: config(),
    additionalFilterState: useSelector((state) => state['statistics/filters'].filters),
    setAction: 'PULSE/SET_FILTER',
    nonClearableFilters: ['date_group_period', 'include_vat'],
    localStorageKey: 'pulse',
  })

  const {
    summary = {},
    metrics,
    items = [],
    loading,
    chartType,
    measure,
  } = useSelector((state) => ({
    summary: state['statistics/pulse'].analytics.data.summary,
    metrics: state['statistics/pulse'].metrics,
    items: state['statistics/pulse'].analytics.data.items,
    loading: state['statistics/pulse'].analytics.loading,
    chartType: state['statistics/pulse'].chartType,
    measure: state['statistics/pulse'].measure,
  }))

  useEffect(
    () =>
      dispatch({
        type: 'PULSE/SETUP_INITIAL_METRICS',
        payload: { metrics: metricsConfig },
      }),
    [],
  )

  useEffect(() => setMetricsConfig(metrics), [metrics])

  const [filtersState, setFiltersState] = useState(omit(filterManager.filters, ['include_vat']))

  useEffect(() => {
    const omittedFilters = omit(filterManager.filters, ['include_vat'])
    !isEqual(filtersState, omittedFilters) && setFiltersState(omittedFilters)
  }, [filterManager.filters])

  useEffect(
    () => filterManager.filters && dispatch({ type: 'PULSE/FETCH_DATA', isDemoMode }),
    [filterManager.filters, isDemoMode],
  )

  useEffect(() => {
    const availabelMerticsKeys = Object.keys(summary)

    if (!metricInited && availabelMerticsKeys.length > 0 && !metrics?.length) {
      setMetricInited(true)
      dispatch({
        type: 'PULSE/SET_NEW_METRIC',
        payload: { metric: availabelMerticsKeys[0], isActive: true },
      })
    }
  }, [summary, metrics])

  const headerSize = useMemo(() => {
    switch (true) {
      case breakpoints.xs:
        return 'Header-40'
      case breakpoints.fb:
        return 'Header-50'
      default:
        return 'Header-60'
    }
  }, [breakpoints])

  return (
    <>
      <Grid className={clsx(styles['header-container'], 'margin-bottom-64')}>
        <Typography variant={headerSize} className={styles['header']}>
          {t('common::nav_menu.pulse')}
        </Typography>

        <Switch
          label={t('statistics::demo_mode_switcher')}
          checked={isDemoMode}
          onChange={() => {
            setIsDemoMode(!isDemoMode)
          }}
          className={styles['demo-switcher']}
        />
      </Grid>
      <div className={'margin-bottom-32'}>
        {filterManager.filters && (
          <FilterPanel filterManager={filterManager} className={styles['pulse__filterPanel']} />
        )}
      </div>

      <Chart
        emptyDataLabel={{
          text: (
            <div className={styles['chart__empty-text']}>
              <div
                dangerouslySetInnerHTML={{
                  __html: t('statistics::chart.empty_data.text', {
                    context: isDemoMode ? 'demo' : '',
                  }),
                }}
              />
              {!isDemoMode && (
                <Button variant={'primary'} size={'m'} onClick={() => setIsDemoMode(true)}>
                  {t('statistics::demo_mode_switcher')}
                </Button>
              )}
            </div>
          ),
        }}
        colors={COLORS}
        className={'margin-bottom-32'}
        chartType={chartType}
        items={items}
        metrics={metrics}
        loading={loading}
        date_group_period={{
          value: filterManager.filters?.date_group_period ?? 'day',
          options: filterManager.configuration.date_group_period.options,
        }}
        include_vat={filterManager.filters?.include_vat}
        summary={summary}
        measure={measure}
        changeChartPeriod={(value) =>
          filterManager.updateFilter({
            fieldName: 'date_group_period',
            value: value,
          })
        }
        toggleMetric={(id) => dispatch({ type: 'PULSE/TOGGLE_METRIC_HIDDEN', payload: { id } })}
        changeChartType={() =>
          dispatch({
            type: 'PULSE/SET_CHART_TYPE',
            payload: { type: chartType === 'Line' ? 'Bar' : 'Line' },
          })
        }
      />
      <Cards className={styles['pulse__cards']} />
    </>
  )
}

Pulse.propTypes = {
  filterPortalRef: PropTypes.object,
}

export default Pulse
